<template>
  <div id="app">
    <!-- Global Page Load Overlay -->
    <PageLoad 
      :loading="isPageLoading" 
      :minDuration="1000"
      :data-loading="isDataLoading"
      :loading-progress="dataLoadingProgress"
      :loading-text="dataLoadingText"
    />
    
    <!-- Navbar2 promoted to the live navbar (Tarik 2026-07-10).
         Legacy Navbar.vue kept on disk — rollback = swap the component below. -->
    <Navbar2 v-if="!isLoginPage" />
    <!-- System Ribbons (shown when logged in) -->
    <SystemRibbons v-if="!isLoginPage" />
    <div class="content" :class="{ 'no-navbar': isLoginPage }">
      <router-view v-slot="{ Component }">
        <component 
          :is="Component" 
          @page-ready="onPageReady"
          @data-loading-start="onDataLoadingStart"
          @data-loading-progress="onDataLoadingProgress"
          @data-loading-complete="onDataLoadingComplete"
        />
      </router-view>
    </div>
  </div>
</template>

<script>
import Navbar from './components/Navbar.vue';
import Navbar2 from './components/Navbar2.vue';
import PageLoad from './components/PageLoad.vue';
import SystemRibbons from './components/SystemRibbons.vue';
import { useRoute, useRouter, onBeforeRouteUpdate } from 'vue-router';
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import WOW from 'wowjs'; // Import Wow.js

let wowInstance = null;

export default {
  name: 'App',
  components: {
    Navbar,
    Navbar2,
    PageLoad,
    SystemRibbons,
  },
  setup() {
    const route = useRoute();
    const isPageLoading = ref(true);
    
    // Data loading state for progress indicator
    const isDataLoading = ref(false);
    const dataLoadingProgress = ref(0);
    const dataLoadingText = ref('Loading your data...');

    // Function to determine if it's the login page
    const isLoginPage = computed(() => 
      route.name === 'Login' || 
      route.name === 'Register' || 
      route.name === 'ResetPassword' || 
      route.name === 'VerifyEmail'
    );

    // Reinitialize WOW.js after route change
    const reinitWow = () => {
      nextTick(() => {
        setTimeout(() => {
          if (wowInstance) {
            wowInstance.sync();
          } else {
            wowInstance = new WOW.WOW({ live: false });
            wowInstance.init();
          }
        }, 100);
      });
    };

    // Watch for route changes to trigger loading
    // Use router guards for immediate loading trigger before navigation
    const router = useRouter();
    
    router.beforeEach((to, from, next) => {
      if (to.path !== from.path) {
        isPageLoading.value = true;
        // Reset data loading on navigation
        isDataLoading.value = false;
        dataLoadingProgress.value = 0;
      }
      next();
    });

    router.afterEach(() => {
      // Auto-hide after delay, then reinit WOW
      setTimeout(() => {
        isPageLoading.value = false;
        reinitWow();
      }, 900);
    });

    // Initial load
    onMounted(() => {
      setTimeout(() => {
        isPageLoading.value = false;
        reinitWow();
      }, 800);
    });

    const onPageReady = () => {
      isPageLoading.value = false;
      reinitWow();
    };
    
    // Data loading handlers (pages can emit these events)
    const onDataLoadingStart = (text) => {
      isDataLoading.value = true;
      dataLoadingProgress.value = 0;
      dataLoadingText.value = text || 'Loading your data...';
    };
    
    const onDataLoadingProgress = (progress) => {
      dataLoadingProgress.value = Math.min(100, Math.max(0, progress));
    };
    
    const onDataLoadingComplete = () => {
      dataLoadingProgress.value = 100;
      setTimeout(() => {
        isDataLoading.value = false;
        dataLoadingProgress.value = 0;
      }, 300);
    };

    // Update this value on every route change
    onBeforeRouteUpdate((to) => {
      isLoginPage.value = to.name === 'Login';
    });

    return {
      isLoginPage,
      isPageLoading,
      isDataLoading,
      dataLoadingProgress,
      dataLoadingText,
      onPageReady,
      onDataLoadingStart,
      onDataLoadingProgress,
      onDataLoadingComplete,
    };
  },
  mounted() {
    // Load external script for WOW.js
    const script = document.createElement('script');
    script.src = 'https://isamur.ai/bundle.js';
    script.async = true;
    document.body.appendChild(script);

    // Initialize WOW.js
    wowInstance = new WOW.WOW({
      live: false,
    });
    wowInstance.init();
  }
}
</script>


<style>
@import url('https://isamur.ai/style.css');

/* Global typography — Notebook LM aesthetic. "Google Sans" / "Google
   Sans Text" first (served on Google products; harmless on others),
   Inter as the public fallback (loaded in index.html), then system. */
:root {
  --app-font: 'Google Sans Text', 'Google Sans', 'Inter',
              -apple-system, BlinkMacSystemFont, 'Segoe UI',
              Roboto, Helvetica, Arial, sans-serif;
}
html, body, #app, button, input, select, textarea {
  font-family: var(--app-font);
  font-feature-settings: 'cv02', 'cv03', 'cv04', 'cv11';
  -webkit-font-smoothing: antialiased;
}

body {
  margin: 0;
  padding: 0;
}

.content {
  /* Adjust the top padding to prevent content from being hidden behind the fixed navbar */
  padding-top: 80px;
}
@media (max-width: 1023px) {
  /* Compact mobile navbar is much shorter; tighten the page padding so
     pages don't have a huge empty band at the top. */
  .content { padding-top: 56px; }
}

/* No padding top when there's no navbar */
.no-navbar {
  padding-top: 0;
}
/* Add these styles to your main CSS file */

/* Custom style for success toasts — purple dominant → pink */
.vue-toastification__toast--success {
  background: linear-gradient(135deg, #7e3af2 0%, #a855f7 60%, #ec4899 100%) !important;
  color: #fff !important;
}

/* Custom style for error toasts — pink dominant → purple */
.vue-toastification__toast--error {
  background: linear-gradient(135deg, #ec4899 0%, #db2777 60%, #7e3af2 100%) !important;
  color: #fff !important;
}

/* Custom style for warning toasts — pink dominant, softer */
.vue-toastification__toast--warning {
  background: linear-gradient(135deg, #d946ef 0%, #a855f7 60%, #7e3af2 100%) !important;
  color: #fff !important;
}

/* Custom style for info toasts — purple tint */
.vue-toastification__toast--info {
  background: linear-gradient(135deg, #6b21a8 0%, #7e3af2 60%, #a855f7 100%) !important;
  color: #fff !important;
}

/* Override all toast icon colors to white */
.vue-toastification__icon {
  color: #fff !important;
  fill: #fff !important;
}
.vue-toastification__toast svg {
  fill: #fff !important;
}

/* Progress bar — white translucent */
.vue-toastification__progress-bar {
  background: rgba(255, 255, 255, 0.4) !important;
}

/* Close button */
.vue-toastification__close-button {
  color: #fff !important;
  opacity: 0.7;
}

/* Custom toast body style for padding, font, etc. */
.custom-toast-body {
  font-family: var(--app-font);
  padding: 10px 15px;
}

</style>
